import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {rehypeGithubAlerts} from "rehype-github-alerts";
import {ClassStylable} from "../../../models/common/ComponentBehavioural";

/**
 * Represents the properties required to render Markdown content.
 *
 * This interface defines the structure for providing the raw Markdown text
 * that will be processed and displayed by a Markdown renderer. It ensures
 * that the renderer has the necessary input for processing Markdown to HTML
 * or other desired formats.
 *
 * @property source - The raw Markdown string to be rendered. Must be a valid string containing Markdown syntax.
 * @property className - Optional CSS class name for styling the Markdown container.
 */
export interface MarkdownRendererProps extends ClassStylable {
    source: string,
}

/**
 * A functional component for rendering Markdown content with extended features.
 *
 * This component provides a simple and customizable way to display Markdown content
 * with additional functionalities such as GitHub-flavored Markdown (GFM) support and
 * plugin extensibility. It ensures that rendered Markdown is formatted with enhanced
 * styling and behaviors for better user experience.
 *
 * @param props An object containing configuration and source content to be rendered.
 *              Must include a `source` property that represents the Markdown string.
 * @returns A JSX element that displays the rendered Markdown content within a container.
 *
 * @throws Will throw an error if `props.source` is not provided or invalid.
 */
function MarkdownRenderer(props: MarkdownRendererProps ) {
    const newProps = {
        ...props,
        plugins: [
            remarkGfm,
           // RemarkMathPlugin,
        ],
    };

    return (
        <div className={ props.className || "markdown-container"}>
            <ReactMarkdown
                remarkPlugins={newProps.plugins}
                rehypePlugins={[rehypeGithubAlerts]}
            >
                { props.source }
            </ReactMarkdown>
        </div>

    );
}

export default MarkdownRenderer;